/**
 * Choix du commercial d'un lead : les comptes actifs qui peuvent suivre des
 * leads, ou le vivier. Reserve a `leads.attribuer` : l'appelant ne l'affiche
 * qu'a qui le peut.
 */
import { useId, useRef } from 'react';
import type { RefObject } from 'react';
import { RefreshCw } from 'lucide-react';
import { aPermission } from '@leads/shared';
import type { MembreAnnuaireDTO } from '@leads/shared';
import { useAuth } from '../lib/auth';
import { useAnnuaire } from '../lib/requetes';
import { BoutonIcone, useRepliDuFocus } from './ui';

/** Un `<option>` ne porte pas `null` : cette valeur represente le vivier. */
export const VALEUR_VIVIER = 'vivier';

/** Comptes pouvant recevoir des leads (conception 3.2), l'utilisateur connecte (`moiId`) en premier. */
export function membresAttribuables(membres: readonly MembreAnnuaireDTO[], moiId?: string): MembreAnnuaireDTO[] {
  const attribuables = membres.filter((membre) => aPermission(membre.role, 'leads.ecrire'));
  return [...attribuables.filter((membre) => membre.id === moiId), ...attribuables.filter((membre) => membre.id !== moiId)];
}

/**
 * Annuaire en echec (modele de l'echec de `/statuts`) : sans ce message, la
 * liste passerait pour une equipe vide. `id` decrit la liste qui en depend ;
 * `refListe` la designe : le focus y passe quand le message part avec le bouton.
 */
export function AnnuaireIndisponible({ id, onRecharger, refListe }: {
  id: string;
  onRecharger: () => void;
  refListe: RefObject<HTMLElement | null>;
}) {
  const refRecharger = useRepliDuFocus(refListe);
  return (
    <span className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-danger-700">
      <span id={id}>Commerciaux indisponibles.</span>
      <BoutonIcone ref={refRecharger} libelle="Recharger les commerciaux" icone={RefreshCw} taille="xs" onClick={onRecharger} />
    </span>
  );
}

export function ChampCommercial({ id, valeur, onChange, nomActuel, desactive = false, className = 'champ', ref, invalide = false, decritPar }: {
  id: string;
  /** Identifiant du commercial ; `null` : vivier. */
  valeur: string | null;
  onChange: (proprietaireId: string | null, nom: string | null) => void;
  /** Nom du commercial actuel, s'il n'est plus dans l'annuaire (compte desactive). */
  nomActuel?: string | null;
  desactive?: boolean;
  className?: string;
  /** Pour y ramener le focus (erreur signalee, choix enregistre). */
  ref?: RefObject<HTMLSelectElement | null>;
  /** Erreur affichee sous la liste par l'appelant, et son identifiant. */
  invalide?: boolean;
  decritPar?: string;
}) {
  const { utilisateur } = useAuth();
  const annuaire = useAnnuaire();
  const idIndisponible = useId();
  const refInterne = useRef<HTMLSelectElement>(null);
  const refListe = ref ?? refInterne;
  const membres = membresAttribuables(annuaire.data ?? [], utilisateur?.id);
  /** Commercial choisi mais absent de l'annuaire, garde en option de repli ; `null` : aucun. */
  const repli: string | null = valeur !== null && !membres.some((membre) => membre.id === valeur) ? valeur : null;
  // Annuaire en echec, un compte actif manque aussi : il n'est pas pour autant desactive.
  const nomRepli = nomActuel ?? (repli === utilisateur?.id ? `${utilisateur.nom} (vous)` : null);
  const libelleRepli = annuaire.isLoading ? 'Chargement…' : (nomRepli ?? (annuaire.isError ? 'Commercial actuel' : 'Compte désactivé'));

  return (
    <>
      <select
        ref={refListe}
        id={id}
        aria-invalid={invalide || undefined}
        aria-describedby={[decritPar, annuaire.isError ? idIndisponible : null].filter(Boolean).join(' ') || undefined}
        className={className}
        value={valeur ?? VALEUR_VIVIER}
        disabled={desactive}
        onChange={(evenement) => {
          const choisi = evenement.target.value;
          if (choisi === VALEUR_VIVIER) onChange(null, null);
          else onChange(choisi, membres.find((membre) => membre.id === choisi)?.nom ?? nomActuel ?? null);
        }}
      >
        <option value={VALEUR_VIVIER}>Non attribué (vivier)</option>
        {membres.map((membre) => (
          <option key={membre.id} value={membre.id}>{membre.id === utilisateur?.id ? `${membre.nom} (vous)` : membre.nom}</option>
        ))}
        {repli !== null && <option value={repli}>{libelleRepli}</option>}
      </select>
      {annuaire.isError && <AnnuaireIndisponible id={idIndisponible} refListe={refListe} onRecharger={() => void annuaire.refetch()} />}
    </>
  );
}
